import {combineLatest, Observable} from 'rxjs';
import {map, shareReplay, switchMap, throttleTime} from 'rxjs/operators';
import {
  contractDefaultTakerFeeRateEr$,
  contractsCache$,
  currenciesCurrencyCache$,
  leverageMarginsCache$,
} from '@/config';
import {contractAirdropPosSubUserIds$, copyTradeUserIds$, subUserIds$} from '@/raw/users';
import {contractAccountsCacheSubs$} from '@/contract-accounts/all/contract-accounts-cache-all';
import {contractMarketRealtimeCache$} from '@/market-contract/market24h-contract-cache';
import {positionsCacheAll$} from './positions-cache-all';
import {fiatEntity$, convertPositions} from '../positions';
import {calcAirdropMarginRatio} from '../margin-ratio';
import type {ContractAccount, Position} from '@/types';

export const positionsAll$ = combineLatest([
  subUserIds$,
  contractAirdropPosSubUserIds$,
  contractsCache$,
  positionsCacheAll$,
  contractMarketRealtimeCache$,
  contractAccountsCacheSubs$,
  fiatEntity$,
  contractDefaultTakerFeeRateEr$,
  leverageMarginsCache$,
  currenciesCurrencyCache$,
]).pipe(
  map(
    ([
      userIds,
      airdropPosSubUserIds,
      contractsCache,
      positionsCacheAll,
      contractMarket24hCache,
      contractAccountsCacheAll,
      fiatEntity,
      defaultTakerFeeRateEr,
      leverageMarginsCache,
      currenciesCache,
    ]) => {
      const airdropMarginRatioCache = airdropPosSubUserIds
        .map((userId) => positionsCacheAll.get(userId))
        .filter(Boolean)
        .reduce((p, c) => [...p, ...Array.from(c.values())], [])
        .map((p) =>
          calcAirdropMarginRatio(
            p,
            currenciesCache,
            leverageMarginsCache,
            contractsCache,
            contractMarket24hCache,
            defaultTakerFeeRateEr
          )
        )
        .filter(Boolean)
        .reduce((res, cur) => res.set(cur.key, cur), new Map());
      return userIds.reduce((acc, userId) => {
        const isAirdrop = airdropPosSubUserIds.includes(userId);
        return acc.set(
          userId,
          convertPositions(
            contractsCache,
            positionsCacheAll.get(userId) || new Map(),
            contractMarket24hCache,
            // @ts-ignore
            contractAccountsCacheAll.get(userId) as Map<string, ContractAccount>,
            fiatEntity,
            isAirdrop ? airdropMarginRatioCache : new Map(), // 子账号不需要计算展示margin ratio, airdrop position 使用额外逻辑计算
            defaultTakerFeeRateEr,
            false,
            false,
            isAirdrop
          )
        );
      }, new Map<number, Position[]>());
    }
  ),
  shareReplay(1)
);

export const copyTradePositionMap$ = filterPositions(copyTradeUserIds$);

export const airdropPositions$ = combineLatest([contractAirdropPosSubUserIds$, positionsAll$]).pipe(
  map(([ids, poss]) => {
    return ids
      .map((id) => poss.get(id))
      .reduce((p, c) => [...p, ...(c || [])], [])
      .sort((a, b) => b.transactTimeNs - a.transactTimeNs);
  })
);

function filterPositions(ids$: Observable<number[]>) {
  return ids$.pipe(
    switchMap((ids) =>
      positionsAll$.pipe(
        map((accounts) => {
          return ids.reduce((acc, id) => {
            return acc.set(id, accounts.get(id) || []);
          }, new Map<number, Position[]>());
        }),
        throttleTime(1000)
      )
    )
  );
}
